// Purpose: browser-side upload pipeline. Generates a DEK, streams the file
// through @ast/crypto-web, PUTs to the SAS URL, then POSTs the raw DEK via a
// caller-supplied complete callback. The DEK only exists in memory for the
// duration of this function.
//
// Phase 5 generalization: the function takes a presign/complete callback
// pair so both the share sender (authenticated, CSRF) and the dropbox
// external uploader (anonymous, no CSRF) can share this pipeline.

import { encryptStream, DEK_BYTES } from '@ast/crypto-web';

export interface UploadProgress {
  fileIndex: number;
  bytesEncrypted: number;
  totalBytes: number;
  stage: 'presign' | 'encrypt-upload' | 'complete' | 'done';
}

export interface UploadResult {
  fileId: string;
  blobPath: string;
}

export interface UploadAdapters {
  // Returns the SAS PUT URL and a server-side fileId the complete call refers
  // back to. Caller is responsible for any CSRF / auth headers.
  // Commit 8b.15: relativePath is optional. Pre-8b.15 callers omit it; the
  // server treats missing as flat-file at the share root.
  presign: (input: {
    originalFilename: string;
    sizeBytes: number;
    mimeType: string;
    relativePath?: string;
  }) => Promise<{ fileId: string; uploadUrl: string; blobPath: string }>;
  // Posts the raw DEK (base64) and any caller-required ids. Returns void on
  // success, throws on failure.
  complete: (input: { fileId: string; rawDekBase64: string }) => Promise<void>;
  // Commit 8b.13: optional. Called when the PUT or complete step fails after
  // presign already minted a server-side Upload row. Implementation posts a
  // short reason to the server's /error endpoint so the row leaves
  // PENDING_UPLOAD and the audit chain captures the failure (verb
  // upload.failed). Best-effort — uploadFile already has a failure to surface
  // to the user, so a failure inside reportError is swallowed (and routed to
  // the optional onReportErrorFailure hook for telemetry) rather than
  // shadowing the original cause.
  reportError?: (input: { fileId: string; errorReason: string }) => Promise<void>;
}

export async function uploadFile(
  file: File,
  fileIndex: number,
  adapters: UploadAdapters,
  onProgress?: (p: UploadProgress) => void,
  relativePath?: string
): Promise<UploadResult> {
  onProgress?.({
    fileIndex,
    bytesEncrypted: 0,
    totalBytes: file.size,
    stage: 'presign',
  });
  const presign = await adapters.presign({
    originalFilename: file.name,
    sizeBytes: file.size,
    mimeType: file.type || 'application/octet-stream',
    ...(relativePath ? { relativePath } : {}),
  });

  // DEK is generated browser-side and never persisted. Lives in memory
  // only until the /complete call returns.
  const dek = new Uint8Array(DEK_BYTES);
  crypto.getRandomValues(dek);

  try {
    const encryptedStream = encryptStream(file.stream(), dek, {
      plaintextSize: file.size,
    });
    // Collect into a single Blob for the upload so the SAS PUT Content-Length
    // is known up front (Azurite requires it). For very large files this
    // holds the ciphertext in memory; Phase 8 can swap to blocked PUT if
    // 2GB uploads become routine.
    const ct = await new Response(encryptedStream).blob();
    onProgress?.({
      fileIndex,
      bytesEncrypted: file.size,
      totalBytes: file.size,
      stage: 'encrypt-upload',
    });

    let putRes: Response;
    try {
      putRes = await fetch(presign.uploadUrl, {
        method: 'PUT',
        headers: { 'x-ms-blob-type': 'BlockBlob' },
        body: ct,
      });
    } catch (err) {
      // Network-level failure (DNS, connection, abort). Report so the row
      // doesn't sit silent in PENDING_UPLOAD.
      const reason = `put_network_error: ${(err as Error).message ?? 'unknown'}`;
      await reportErrorBestEffort(adapters, presign.fileId, reason);
      throw new Error(`blob upload failed: ${(err as Error).message ?? 'network error'}`);
    }
    if (!putRes.ok) {
      const reason = `put_http_${putRes.status}`;
      await reportErrorBestEffort(adapters, presign.fileId, reason);
      throw new Error(`blob upload failed: HTTP ${putRes.status}`);
    }

    onProgress?.({
      fileIndex,
      bytesEncrypted: file.size,
      totalBytes: file.size,
      stage: 'complete',
    });
    const rawDekBase64 = bytesToBase64(dek);
    try {
      await adapters.complete({ fileId: presign.fileId, rawDekBase64 });
    } catch (err) {
      // PUT succeeded but the server-side wrap-DEK / status flip failed.
      // The blob is in storage but the row is still PENDING_UPLOAD; report
      // so the operator sees a real terminal state.
      const reason = `complete_failed: ${(err as Error).message ?? 'unknown'}`;
      await reportErrorBestEffort(adapters, presign.fileId, reason);
      throw err;
    }
    onProgress?.({
      fileIndex,
      bytesEncrypted: file.size,
      totalBytes: file.size,
      stage: 'done',
    });

    return { fileId: presign.fileId, blobPath: presign.blobPath };
  } finally {
    dek.fill(0);
  }
}

// Best-effort error reporting. Swallows failures of the report itself so the
// real cause makes it back to the caller. Console-warns for browser-devtools
// visibility when the report path is itself broken.
async function reportErrorBestEffort(
  adapters: UploadAdapters,
  fileId: string,
  errorReason: string
): Promise<void> {
  if (!adapters.reportError) return;
  try {
    await adapters.reportError({ fileId, errorReason });
  } catch (err) {
    console.warn('upload.reportError failed', { fileId, errorReason, err });
  }
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]!);
  return btoa(binary);
}
